import { runtimeEnv } from './runtimeEnv';
export type DeployEnvironment = 'development' | 'staging' | 'production';

export interface DeployEnvironmentInputs {
  /** Vite's build mode. */
  mode?: string;
  /** The backend this build was baked against (`VITE_BACKEND_URL`). */
  backendUrl?: string;
  /** The host the page is currently served from. */
  hostname?: string;
}

/**
 * Which deployment a build belongs to, from explicit inputs.
 *
 * The backend the build was baked against decides, not the hostname the page
 * happens to be served from: a hostname carries no environment during the
 * lureo.app -> create.lureo.com move (lureo-docs#348), when the `.com` hosts are
 * parked on the staging services for the rehearsal. Reading `production` there
 * made the staging build load the production feature flags (`environment_types`,
 * so `keyframes_on` came back unset) and tag Sentry as production. Hostname stays
 * the fallback for builds with no backend URL baked in.
 */
export function resolveDeployEnvironment({ mode, backendUrl, hostname }: DeployEnvironmentInputs): DeployEnvironment {
  const host = (hostname ?? '').toLowerCase();
  if (mode === 'development' || host === 'localhost' || host === '127.0.0.1') return 'development';

  const backend = (backendUrl ?? '').toLowerCase();
  if (backend.includes('localhost')) return 'development';
  if (backend.includes('staging')) return 'staging';
  if (backend.includes('production') || /^https?:\/\/backend\.lureo\.com(\/|$)/.test(backend)) return 'production';

  if (host.includes('staging')) return 'staging';
  if (host.includes('dev')) return 'development';
  return 'production';
}

/** {@link resolveDeployEnvironment} for the running build. */
export function getDeployEnvironment(): DeployEnvironment {
  return resolveDeployEnvironment({
    mode: import.meta.env?.MODE,
    backendUrl: runtimeEnv.VITE_BACKEND_URL as string | undefined,
    hostname: typeof window !== 'undefined' ? window.location.hostname : '',
  });
}
